import * as React from 'react';

import {
  Button,
  cn,
  Col,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Row,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideBell, LucideCheckCheck } from 'lucide-react';

import { NotificationList } from './NotificationList';

import { NotificationBadge } from '~/app/components/NotificationBadge';
import { notificationMarkAllAsReadMutationAtom } from '~/modules/notifications/_graphql/mutationNotificationMarkAllAsRead';
import { notificationMarkAsReadMutationAtom } from '~/modules/notifications/_graphql/mutationNotificationMarkAsRead';
import { notificationMarkAsSeenMutationAtom } from '~/modules/notifications/_graphql/mutationNotificationMarkAsSeen';
import {
  $notificationFeed,
  $notificationFeedCursor,
  $notificationFeedEntries,
} from '~/modules/notifications/_state/notificationFeed.state';
import { $notificationUnreadCount } from '~/modules/notifications/_state/unreadCount.state';
import { useNotificationReceivedSubscription } from '~/modules/notifications/hooks/useNotificationReceivedSubscription';
import { useTranslations } from '~/modules/notifications/i18n/useTranslations';
import { NotificationFeedEntry } from '~/modules/notifications/types';
import { $location } from '~/shared/state';

const MAX_ENTRIES = 100;

const $markAsRead = notificationMarkAsReadMutationAtom();
const $markAllAsRead = notificationMarkAllAsReadMutationAtom();
const $markAsSeen = notificationMarkAsSeenMutationAtom();

export const NotificationCenter = () => {
  const t = useTranslations();
  const entriesRef = React.useRef<NotificationFeedEntry[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);

  const feedQuery = useAtomValue($notificationFeed);
  const [entries, setEntries] = useAtom($notificationFeedEntries);
  const setCursor = useSetAtom($notificationFeedCursor);
  const unreadCountQuery = useAtomValue($notificationUnreadCount);

  const markAsRead = useSetAtom($markAsRead);
  const markAllAsRead = useSetAtom($markAllAsRead);
  const markAsSeen = useSetAtom($markAsSeen);
  const setLocation = useSetAtom($location);

  useNotificationReceivedSubscription(undefined, {
    onEvent: (entry: NotificationFeedEntry) => {
      setEntries((prev) => {
        if (prev.some((e) => e.id === entry.id)) return prev;
        return [entry, ...prev].slice(0, MAX_ENTRIES);
      });
      unreadCountQuery.refetch();
    },
  });

  const unreadCount = unreadCountQuery.data ?? 0;
  const feedData = feedQuery.data;
  const isLoading = feedQuery.loadingState.isInitialLoading;

  entriesRef.current = entries;

  const handleMarkAsRead = React.useCallback(
    async (id: string) => {
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, isRead: true } : e)),
      );
      try {
        await markAsRead({ ids: [id] });
        unreadCountQuery.refetch();
      } catch {
        setEntries((prev) =>
          prev.map((e) => (e.id === id ? { ...e, isRead: false } : e)),
        );
      }
    },
    [markAsRead, setEntries, unreadCountQuery],
  );

  const handleMarkAllAsRead = React.useCallback(async () => {
    const previousEntries = entries;
    setEntries((prev) => prev.map((e) => ({ ...e, isRead: true })));
    try {
      await markAllAsRead({});
      unreadCountQuery.refetch();
    } catch {
      setEntries(previousEntries);
    }
  }, [entries, markAllAsRead, setEntries, unreadCountQuery]);

  const handleLoadMore = React.useCallback(() => {
    if (entries.length > 0) {
      setCursor({ afterId: entries[entries.length - 1].id });
    }
  }, [entries, setCursor]);

  const handleNavigate = React.useCallback(
    (actionLink: string) => {
      if (actionLink.startsWith('/') && !actionLink.startsWith('//')) {
        setIsOpen(false);
        setLocation({
          pathname: actionLink,
          searchParams: new URLSearchParams(),
        });
        return;
      }
      try {
        const url = new URL(actionLink, window.location.origin);
        if (url.origin === window.location.origin) {
          setIsOpen(false);
          setLocation({
            pathname: url.pathname,
            searchParams: new URLSearchParams(url.search),
          });
        }
      } catch {
        // Invalid URL
      }
    },
    [setIsOpen, setLocation],
  );

  React.useEffect(() => {
    if (feedData?.entries) {
      setEntries((prev) => {
        if (prev.length === 0) return feedData.entries.slice(0, MAX_ENTRIES);
        const existingIds = new Set(prev.map((e) => e.id));
        const newEntries = feedData.entries.filter(
          (e) => !existingIds.has(e.id),
        );
        return [...prev, ...newEntries].slice(0, MAX_ENTRIES);
      });
    }
  }, [feedData, setEntries]);

  React.useEffect(() => {
    if (isOpen) {
      const current = entriesRef.current;
      const unseenIds = current.filter((e) => !e.isSeen).map((e) => e.id);
      if (unseenIds.length > 0) {
        setEntries((prev) =>
          prev.map((e) =>
            unseenIds.includes(e.id) ? { ...e, isSeen: true } : e,
          ),
        );
        (async () => {
          try {
            await markAsSeen({ ids: unseenIds });
          } catch {
            setEntries((prev) =>
              prev.map((e) =>
                unseenIds.includes(e.id) ? { ...e, isSeen: false } : e,
              ),
            );
          }
        })();
      }
    }
  }, [isOpen, markAsSeen, setEntries]);

  return (
    <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <div className="relative flex items-center gap-2">
        <Button
          variant="solid"
          size="icon"
          color="primary"
          aria-label={t.notifications.title}
          className="border border-white/30 bg-white/10 text-white"
          icon={<LucideBell />}
        />
        {!isOpen && <NotificationBadge count={unreadCount} />}
      </div>
      <Popover placement="bottom end" offset={8}>
        <PopoverDialog>
          <Col className="w-[380px]">
            <Row
              xBetween
              yCenter
              className="border-b border-neutral/10 px-4 py-3"
            >
              <span className="text-sm font-semibold">
                {t.notifications.title}
              </span>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className={cn(
                    'flex items-center gap-1 text-xs text-primary',
                    'hover:text-primary/80',
                  )}
                >
                  <LucideCheckCheck className="size-3.5" />
                  {t.notifications.markAllAsRead}
                </button>
              )}
            </Row>
            <NotificationList
              entries={entries}
              isLoading={isLoading}
              hasError={!!feedQuery.error}
              hasMore={feedData?.hasMore ?? false}
              onLoadMore={handleLoadMore}
              onMarkAsRead={handleMarkAsRead}
              onNavigate={handleNavigate}
            />
          </Col>
        </PopoverDialog>
      </Popover>
    </PopoverTrigger>
  );
};
